import { useLayoutEffect, useState } from 'react';

/**
 * The map frame shared by the callout's map and the day's route map: tiles
 * composed straight from the slippy-map grid, and everything else projected
 * into the same pixel space. The math is the standard Web Mercator tile
 * projection. Moved here from EventMap unchanged.
 *
 * Tiles are Stamen Terrain, hosted by Stadia Maps. Stadia serves localhost
 * without credentials for development; any deployed hostname needs a free API
 * key in VITE_STADIA_API_KEY.
 */

export const TILE = 256;
/**
 * What the frame is assumed to be until it has been measured.
 *
 * The frame is `width: 100%`, so its real width is the callout's, which
 * changes with the window. Everything below — the zoom, the tile grid, the
 * outline path — is computed from the measured size; these are only the first
 * render's guess, replaced before the browser paints.
 */
export const WIDTH = 408;
// Tall enough that a whole borough outline is legible, not a smear.
export const HEIGHT = 216;
const MAX_ZOOM = 16;
// Low enough to fit Queens or Staten Island end to end.
const MIN_ZOOM = 9;
/** A lone address, zoomed out two steps so the surrounding streets read. */
const SINGLE_PIN_ZOOM = 13;

const API_KEY = import.meta.env.VITE_STADIA_API_KEY as string | undefined;


export function tileUrl(z: number, x: number, y: number): string {
  const base = `https://tiles.stadiamaps.com/tiles/stamen_terrain/${z}/${x}/${y}@2x.png`;
  return API_KEY ? `${base}?api_key=${API_KEY}` : base;
}

/** World pixel coordinates at a given zoom. */
export function project(lat: number, lng: number, zoom: number): { x: number; y: number } {
  const scale = TILE * 2 ** zoom;
  const sinLat = Math.sin((lat * Math.PI) / 180);
  return {
    x: ((lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale,
  };
}

export interface Placed {
  left: number;
  top: number;
}

/** The frame's own pixel size: the viewport everything below is projected into. */
export interface Size {
  width: number;
  height: number;
}

/**
 * The frame's size, watched.
 *
 * The projection has to be built against the box the tiles are actually laid
 * into, not a constant. A callback ref rather than `useRef`, so it also fires
 * when a map that returned nothing on one event renders on the next.
 */
export function useFrameSize(): [(node: HTMLElement | null) => void, Size] {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [size, setSize] = useState<Size>({ width: WIDTH, height: HEIGHT });

  useLayoutEffect(() => {
    if (!node) return;
    // The padding box, which is what `inset: 0` and the tiles' offsets resolve
    // against — the border is outside it.
    //
    // A zero reading means the frame is hidden rather than flat, and fitting a
    // map to nothing lands on the minimum zoom: keep the last real size and
    // wait for the observer to report a live one.
    const read = () => {
      const width = node.clientWidth;
      const height = node.clientHeight;
      if (width <= 0 || height <= 0) return;
      setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [setNode, size];
}

export interface Frame {
  zoom: number;
  originX: number;
  originY: number;
  tiles: { key: string; url: string; left: number; top: number }[];
}

/**
 * Chooses the largest zoom at which every point still fits in the viewport.
 *
 * `padding` is the room left around the content. Outlines need much more than
 * pins do — a borough drawn edge to edge is unreadable, and the whole shape has
 * to be visible for the outline to mean anything.
 */
export function fitZoom(
  points: [number, number][],
  size: Size,
  padX = 76,
  padY = 64,
): number {
  if (points.length < 2) return SINGLE_PIN_ZOOM;
  for (let z = MAX_ZOOM; z >= MIN_ZOOM; z -= 1) {
    const projected = points.map(([lat, lng]) => project(lat, lng, z));
    const w = Math.max(...projected.map((p) => p.x)) - Math.min(...projected.map((p) => p.x));
    const h = Math.max(...projected.map((p) => p.y)) - Math.min(...projected.map((p) => p.y));
    if (w < size.width - padX && h < size.height - padY) return z;
  }
  return MIN_ZOOM;
}

export function buildFrame(
  points: [number, number][],
  size: Size,
  padX?: number,
  padY?: number,
): { frame: Frame; placed: Placed[] } {
  const zoom = fitZoom(points, size, padX, padY);
  const projected = points.map(([lat, lng]) => project(lat, lng, zoom));

  const centerX = (Math.min(...projected.map((p) => p.x)) + Math.max(...projected.map((p) => p.x))) / 2;
  const centerY = (Math.min(...projected.map((p) => p.y)) + Math.max(...projected.map((p) => p.y))) / 2;

  // Top-left of the viewport, in world pixels.
  const originX = centerX - size.width / 2;
  const originY = centerY - size.height / 2;

  const firstCol = Math.floor(originX / TILE);
  const lastCol = Math.floor((originX + size.width) / TILE);
  const firstRow = Math.floor(originY / TILE);
  const lastRow = Math.floor((originY + size.height) / TILE);

  const span = 2 ** zoom;
  const tiles: Frame['tiles'] = [];
  for (let col = firstCol; col <= lastCol; col += 1) {
    for (let row = firstRow; row <= lastRow; row += 1) {
      if (row < 0 || row >= span) continue;
      const wrapped = ((col % span) + span) % span;
      tiles.push({
        key: `${zoom}/${wrapped}/${row}`,
        url: tileUrl(zoom, wrapped, row),
        left: col * TILE - originX,
        top: row * TILE - originY,
      });
    }
  }

  return {
    frame: { zoom, originX, originY, tiles },
    placed: projected.map((p) => ({ left: p.x - originX, top: p.y - originY })),
  };
}
